'use client';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { useSession } from '@/hooks/useSessionHook';
import { useStartConversation } from '@/hooks/use-start-conversation';
import supabase from '@/supabase';
import TopHeader from '@/components/TopHeader';
import BottomNav from '@/components/BottomNav';

interface FriendProfile {
  id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  description: string | null;
}

export default function FriendProfilePage() {
  const { friendId } = useParams<{ friendId: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;

  const [profile, setProfile] = useState<FriendProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { startConversation, isLoading: isStartingConversation, error: conversationError } = useStartConversation(userId);

  useEffect(() => {
    const fetchFriendProfile = async () => {
      if (!friendId) {
        setError('Friend ID is required');
        setIsLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .rpc('get_accessible_profiles', { p_profile_ids: [friendId] });

        if (fetchError) {
          throw fetchError;
        }

        if (!data || data.length === 0) {
          throw new Error('Profile not available');
        }

        setProfile(data[0]);
      } catch (err) {
        console.error('Error fetching friend profile:', err);
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFriendProfile();
  }, [friendId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSendMessage = async () => {
    if (!friendId) return;

    const result = await startConversation(friendId);

    if (result.success && result.conversation_id) {
      navigate(`/chat/${result.conversation_id}`);
    }
  };

  const getInitials = (name: string | null): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (isLoading) {
    return (
      <div className="jr-app-shell">
        <TopHeader />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--jr-accent)]" />
        </div>
        <BottomNav />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="jr-app-shell">
        <TopHeader />
        <header className="jr-subheader">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="mr-2 rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
          >
            <ChevronLeft size={24} />
          </Button>
          <h2 className="jr-display text-2xl font-semibold">Profile</h2>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="jr-status-error">{error || 'Profile not found'}</p>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="jr-app-shell">
      <TopHeader />

      <header className="jr-subheader">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleBack}
          className="mr-2 rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
        >
          <ChevronLeft size={24} />
        </Button>
        <h2 className="jr-display text-2xl font-semibold">Profile</h2>
      </header>

      <main className="flex-1 overflow-y-auto pb-24">
        <div className="jr-screen-pad py-5">
          <div className="jr-card mb-4 flex items-start gap-4 p-6">
            <Avatar className="jr-avatar-ring h-28 w-28">
              <AvatarImage
                src={profile.avatar_url || undefined}
                alt={profile.display_name || profile.username || 'User'}
              />
              <AvatarFallback className="bg-[color:var(--jr-surface-muted)] text-2xl text-[color:var(--jr-accent)]">
                {getInitials(profile.display_name || profile.username)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="jr-kicker mb-2">Profile</p>
              <h1 className="jr-section-title text-[2.1rem]">
                {profile.display_name || profile.username || 'Unknown User'}
              </h1>
              <p className="mt-2 text-sm leading-6 text-[var(--jr-text-soft)]">
                {profile.description || 'No description available'}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              data-testid="send-message-button"
              onClick={handleSendMessage}
              disabled={isStartingConversation}
              className="jr-action-primary h-auto w-full min-h-12 text-base"
            >
              {isStartingConversation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting Conversation...
                </>
              ) : (
                'Send Message'
              )}
            </Button>

            <Button
              variant="default"
              className="jr-action-secondary h-auto w-full min-h-12 text-base"
              disabled
            >
              Call in App
            </Button>

            <Button
              variant="secondary"
              className="jr-action-secondary h-auto w-full min-h-12 text-base"
              disabled
            >
              Mute Friend
            </Button>

            <Button
              variant="secondary"
              className="jr-action-secondary h-auto w-full min-h-12 text-base"
              disabled
            >
              Unmute Friend
            </Button>

            <Button
              variant="default"
              className="jr-action-danger h-auto w-full min-h-12 text-base"
              disabled
            >
              Remove Friend
            </Button>

            <Button
              variant="default"
              className="jr-action-danger h-auto w-full min-h-12 text-base"
              disabled
            >
              Block User
            </Button>

            <Button
              variant="outline"
              className="jr-action-secondary h-auto w-full min-h-12 text-base"
              disabled
            >
              Report Problem
            </Button>

            {conversationError && (
              <p className="jr-status-error mt-2 text-center">
                {conversationError}
              </p>
            )}
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
